/**
 * Чистые функции вкладок «Аналитики» HUB 2.0 (волна W5) — без React, со
 * спеками в analytics-utils.test.ts.
 */
import type { AnalyticsPointRow, FunnelDto } from '../../lib/api/admin';

export const ANALYTICS_TABS = ['network', 'points', 'effect', 'tests', 'content', 'freshness'] as const;
export type AnalyticsTab = (typeof ANALYTICS_TABS)[number];

/** ?tab= → вкладка (неизвестное — «Обучение сети»). */
export function tabOf(raw: string | null | undefined): AnalyticsTab {
  return (ANALYTICS_TABS as readonly string[]).includes(raw ?? '') ? (raw as AnalyticsTab) : 'network';
}

export interface FunnelStep {
  key: keyof FunnelDto;
  label: string;
  value: number;
  /** Доля от первого шага, % (у первого — 100). */
  pct: number;
  /** Потеря относительно предыдущего шага (у первого — 0). */
  lost: number;
}

const FUNNEL_LABELS: Array<[keyof FunnelDto, string]> = [
  ['assigned', 'Назначено'],
  ['started', 'Начали'],
  ['completed', 'Завершили'],
  ['certified', 'Получили сертификат'],
];

/** Воронка «назначено → начато → завершено → сертификат» с долями и потерями. */
export function funnelSteps(f: FunnelDto): FunnelStep[] {
  const first = f.assigned;
  return FUNNEL_LABELS.map(([key, label], i) => {
    const value = f[key];
    const prev = i === 0 ? value : f[FUNNEL_LABELS[i - 1][0]];
    return { key, label, value, pct: first > 0 ? Math.round((value / first) * 100) : 0, lost: i === 0 ? 0 : Math.max(0, prev - value) };
  });
}

/** Шаг с наибольшей потерей (без шага «сертификат», если сертификатов у курсов нет — его не считаем). */
export function biggestLoss(steps: FunnelStep[]): FunnelStep | null {
  const cands = steps.slice(1).filter((s) => s.lost > 0);
  if (!cands.length) return null;
  return cands.reduce((a, b) => (b.lost > a.lost ? b : a));
}

export interface CityCard {
  city: string;
  points: number;
  learners: number;
  /** Среднее завершение назначений по точкам с назначениями, %; null — назначений нет. */
  completionPct: number | null;
  overdue: number;
}

/** Карточки городов вкладки «По точкам»: точки без города — «Без города». */
export function cityCards(rows: AnalyticsPointRow[]): CityCard[] {
  const by = new Map<string, AnalyticsPointRow[]>();
  for (const r of rows) {
    const c = (r.city ?? '').trim() || 'Без города';
    by.set(c, [...(by.get(c) ?? []), r]);
  }
  return [...by.entries()]
    .map(([city, rs]) => {
      const withAsg = rs.filter((r) => r.completionPct != null);
      return {
        city,
        points: rs.length,
        learners: rs.reduce((s, r) => s + (r.learners ?? 0), 0),
        completionPct: withAsg.length ? Math.round(withAsg.reduce((s, r) => s + (r.completionPct ?? 0), 0) / withAsg.length) : null,
        overdue: rs.reduce((s, r) => s + (r.overdue ?? 0), 0),
      };
    })
    .sort((a, b) => b.points - a.points || a.city.localeCompare(b.city, 'ru'));
}

/** Цвет доли: ≥ 75 — зелёный, ≥ 60 — оранжевый, ниже — красный (как в прототипе). */
export function pctTone(v: number | null | undefined): 'green' | 'orange' | 'red' | 'muted' {
  if (v == null) return 'muted';
  return v >= 75 ? 'green' : v >= 60 ? 'orange' : 'red';
}

/** Сумма гистограмм баллов курсов (корзины 0–9 … 90–100). */
export function sumHistograms(hs: Array<number[] | undefined>): number[] {
  const out = new Array<number>(10).fill(0);
  for (const h of hs) if (h) h.forEach((n, i) => (out[i] += n || 0));
  return out;
}

/** Корзина проходного балла (для отметки на гистограмме). */
export function passBucket(passingScore: number | null | undefined): number | null {
  if (passingScore == null) return null;
  return Math.min(9, Math.floor(Math.max(0, Math.min(100, passingScore)) / 10));
}

/** «1 240» / «—». */
export function fmtNum(v: number | null | undefined): string {
  return v == null ? '—' : Math.round(v).toLocaleString('ru-RU');
}

/** «87 %» / «—». */
export function fmtPct(v: number | null | undefined): string {
  return v == null ? '—' : `${Math.round(v)} %`;
}
